/**
 * TEF Canada listening (D-109, D-135), as the official booklet describes it: 40 questions in 8 sections,
 * the documents start by themselves, each is heard ONCE and the learner can never go back. For
 * each document: time to read the questions and the options (reading time of the format table),
 * the document once (the generated audio, else the device's voices, one per character, labels
 * never read), then the answer time; the document then closes by itself. Training mode: a full
 * player instead (pause, back, listen again at will), nothing starts or closes by itself (D-144).
 */

import { academyAttemptsPartCreate, academyAttemptsPassageAudioRetrieve } from "@agencyos/api-client";
import { type TefState, listeningPhase } from "@agencyos/core";
import { Button } from "@agencyos/ui-web";
import { ArrowRight, Headphones, Play } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { readStorage, writeStorage } from "../../lib/storage";
import { OnceAudio, speechAvailable, stopSpeaking, unlockOnFirstGesture } from "../audio";
import { ExamAbandon, ExamAudioPanel, ExamBar, ExamCountdown, ExamNav, ExamPill, sequenceCells } from "../ExamFrame";
import { ExamPictureView } from "../ExamPicture";
import { QuestionBlock, useAnswers } from "../ielts/IeltsQuestions";
import { speakLines, stopLines } from "../listening";
import { TrainingListen, TrainingNote } from "../ListeningReplay";
import { SectionDone } from "../McqSection";
import { clock } from "../text";

const WAVE = [0, 1, 2, 3, 4, 5, 6, 7, 8];
const heardKey = (attemptId: string, passageId: string) => `academy.tef.heard.${attemptId}.${passageId}`;

type Listen = "waiting" | "playing" | "blocked" | "heard";

export function TefListening({
  attemptId,
  state,
  training = false,
  onChanged,
}: {
  attemptId: string;
  state: TefState;
  training?: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const group = state.current;
  const passage = group?.passage ?? null;
  const questions = useMemo(() => group?.questions ?? [], [group]);
  const passageKey = passage?.id ?? `doc-${state.part}`;
  const [listen, setListen] = useState<Listen>(() => (readStorage(heardKey(attemptId, passageKey)) === "1" ? "heard" : "waiting"));
  const [elapsed, setElapsed] = useState(0);
  const startedAt = useRef(Date.now());
  const [endedAt, setEndedAt] = useState<number | null>(listen === "heard" ? 0 : null);
  const [audioProblem, setAudioProblem] = useState(false);
  const [moving, setMoving] = useState(false);
  const audio = useRef<OnceAudio | null>(null);
  const started = useRef(listen !== "waiting");
  const closed = useRef(false);
  const { set, flush, valueOf, isAnswered, saves } = useAnswers(attemptId, onChanged);

  useEffect(() => {
    // the clock reads the real time: a hidden tab (timers slowed to one a minute) keeps the
    // reading and answer times of the day of the test (D-114)
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt.current) / 1000));
    const timer = window.setInterval(tick, 500);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  useEffect(() => {
    unlockOnFirstGesture(); // iOS: the documents then start by themselves
    return () => {
      audio.current?.stop();
      stopSpeaking();
      stopLines();
    };
  }, []);

  const elapsedRef = useRef(0);
  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  const heard = useCallback(() => {
    writeStorage(heardKey(attemptId, passageKey), "1");
    setListen("heard");
    setEndedAt((value) => value ?? elapsedRef.current);
  }, [attemptId, passageKey]);

  const play = useCallback(async () => {
    if (started.current) return;
    started.current = true;
    // Marked as heard as soon as it starts: a reload does not give a second listening.
    writeStorage(heardKey(attemptId, passageKey), "1");
    setListen("playing");
    if (passage?.hasAudio) {
      try {
        const response = await academyAttemptsPassageAudioRetrieve(attemptId, passage.id);
        const player = new OnceAudio({
          onEnded: heard,
          onFailed: () => {
            setAudioProblem(true);
            heard();
          },
        });
        audio.current = player;
        // blocked by the browser (Safari without a gesture): the learner starts it, once
        if ((await player.start(response.data.url)) === "blocked") setListen("blocked");
      } catch {
        setAudioProblem(true);
        heard();
      }
      return;
    }
    const lines = state.document?.lines ?? [];
    if (!speechAvailable() || !lines.length) {
      setAudioProblem(true);
      heard();
      return;
    }
    const outcome = await speakLines(lines, { lang: "fr", preferred: "fr-FR" });
    if (outcome === "blocked") {
      setListen("blocked"); // nothing was heard: the button reads it
      return;
    }
    heard();
  }, [attemptId, passageKey, passage, state.document, heard]);

  /** « Listen » after a blocked start: the same single listening (the link already given). */
  const resume = async () => {
    setListen("playing");
    if (audio.current) {
      if ((await audio.current.resume()) === "blocked") setListen("blocked");
      return;
    }
    const outcome = await speakLines(state.document?.lines ?? [], { lang: "fr", preferred: "fr-FR" });
    if (outcome === "blocked") setListen("blocked");
    else heard();
  };

  const next = useCallback(async () => {
    if (closed.current) return;
    closed.current = true;
    setMoving(true);
    try {
      audio.current?.stop();
      stopLines();
      await flush(questions);
      await academyAttemptsPartCreate(attemptId);
    } catch {
      // Already closed (time up): the refreshed state shows what comes next.
    } finally {
      await onChanged();
      setMoving(false);
    }
  }, [attemptId, flush, onChanged, questions]);

  const phase = listeningPhase(elapsed, state.flow, { started: listen !== "waiting", endedAt });
  // The documents start by themselves once the reading time is over, and close after the answer
  // time; in training the learner plays, pauses and moves on when ready (D-144).
  useEffect(() => {
    if (training) return;
    if (phase === "listening" && listen === "waiting") void play();
    if (phase === "closed") void next();
  }, [phase, listen, play, next, training]);
  const passageId = passage?.id ?? "";
  const loadAudio = useCallback(
    async () => (await academyAttemptsPassageAudioRetrieve(attemptId, passageId)).data.url,
    [attemptId, passageId],
  );

  if (!group) return <SectionDone onContinue={() => void next()} busy={moving} />;
  const readingLeft = Math.max(state.flow.readingSeconds - elapsed, 0);
  const answerLeft = endedAt === null ? state.flow.answerSeconds : Math.max(state.flow.answerSeconds - (elapsed - endedAt), 0);
  const typeLabel = state.document?.type ? t(`tef.doc.${state.document.type}`, { defaultValue: state.document.type }) : "";

  return (
    <div className="ac-listening ac-tef-listening">
      <section className="ac-question__sheet ac-listening__head" aria-labelledby="tef-doc-title">
        <div className="ac-listening__intro">
          <p className="ac-player__eyebrow ao-num">
            {t("tef.player.documentOf", { n: state.part, total: state.parts || 1 })}
            {passage?.number ? ` · ${t("tef.player.sectionN", { n: passage.number })}` : ""}
          </p>
          <h2 id="tef-doc-title" className="ac-title-3">
            {typeLabel || t("tef.player.documentN", { n: state.part })}
          </h2>
          {state.instruction && (
            <p className="ac-prompt" lang="fr">
              {state.instruction}
            </p>
          )}
          {passage?.context && (
            <p className="ac-secondary" lang="fr">
              {passage.context}
            </p>
          )}
          <ExamPictureView picture={passage?.image ?? null} lang="fr" />
        </div>
        <ExamAudioPanel
          playing={!training && listen === "playing" && Boolean(passage?.hasAudio)}
          note={
            training ? (
              <TrainingNote />
            ) : (
              <>
                <p className="ac-footnote">{t("tef.player.once")}</p>
                <p className="ac-footnote">{t("listening.training.tip")}</p>
              </>
            )
          }
        >
        {training ? (
          <div lang="fr">
            <TrainingListen
              load={passage?.hasAudio ? loadAudio : null}
              lines={state.document?.lines ?? []}
              onStart={() => {
                if (listen !== "heard") heard();
              }}
            />
          </div>
        ) : (
        <div className={`ac-listen ac-listen--${listen === "heard" ? "done" : listen === "playing" ? "playing" : "idle"}`}>
          {phase === "reading" && (
            <p className="ac-status" role="timer" aria-live="polite">
              {t("tef.player.readFirst", { time: clock(readingLeft) })}
            </p>
          )}
          {listen === "blocked" && (
            <button type="button" className="ac-listen__button" onClick={() => void resume()}>
              <span className="ac-listen__disc" aria-hidden="true">
                <Play size={30} strokeWidth={2.2} />
              </span>
              <span className="ac-listen__label">{t("academy.player.listen")}</span>
            </button>
          )}
          {listen === "playing" && (
            <div className="ac-listen__playing" role="status">
              <span className="ac-wave" aria-hidden="true">
                {WAVE.map((n) => (
                  <span key={n} style={{ animationDelay: `${n * -0.13}s` }} />
                ))}
              </span>
              <span>{t("tef.player.playing")}</span>
            </div>
          )}
          {listen === "heard" && (
            <p className="ac-status ac-status--success" role="timer" aria-live="polite">
              <Headphones aria-hidden size={16} />
              {t("tef.player.answerNow", { time: clock(answerLeft) })}
            </p>
          )}
          {audioProblem && <p className="ko ac-footnote">{t("academy.player.audioProblem")}</p>}
        </div>
        )}
        </ExamAudioPanel>
      </section>

      <section className="ac-question__sheet" aria-label={t("ielts.player.questions")}>
        <ol className="ac-iq-list">
          {questions.map((question, index) => (
            <QuestionBlock
              key={question.id}
              skill="co"
              exam="tef_canada"
              docType={state.document?.type ?? ""}
              question={question}
              fallbackNumber={index + 1}
              value={valueOf(question)}
              onChange={(value) => set(question, value)}
              saveState={saves[question.id]}
            />
          ))}
        </ol>
      </section>

      <ExamNav
        cells={sequenceCells(
          state.total,
          questions.map((q, index) => ({ number: q.number ?? state.answered + index + 1, answered: isAnswered(q) })),
          state.answered + 1,
        )}
      />
      <ExamBar
        hint={t("tef.player.noBack")}
        left={
          !training && phase === "reading" ? (
            <ExamCountdown seconds={readingLeft} label={t("examFrame.readingTime")} tone="reading" />
          ) : !training && listen === "heard" ? (
            <ExamCountdown seconds={answerLeft} label={t("examFrame.answerTime")} />
          ) : null
        }
        pills={<ExamPill>{t("tef.player.documentOf", { n: state.part, total: state.parts || 1 })}</ExamPill>}
        right={
          <>
            {!training && <ExamAbandon />}
            <Button size="lg" variant={listen === "heard" ? "primary" : "secondary"} disabled={moving || listen !== "heard"} onClick={() => void next()}>
              {state.part >= state.parts ? t("tef.player.finishListening") : t("tef.player.nextDocument")}
              <ArrowRight aria-hidden size={18} />
            </Button>
          </>
        }
      />
    </div>
  );
}
